/**
 * Small helpers for the session-recording screens.
 *
 * Kept apart from the pages so the two things a reader depends on are tested on their own:
 * the clock a passage is shown at, and the link that starts the audio at that second.
 */

/** `3725.4` -> `1:02:05`; under an hour `125` -> `2:05`. */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * CADE's public URL for a recording with a media fragment, so a browser that opens it starts
 * the audio at that second. `null` when the recording has no URL: no link is better than a
 * link that goes nowhere.
 */
export function audioAt(url: string | null | undefined, seconds: number): string | null {
  if (!url) return null;
  return `${url.split('#')[0]}#t=${Math.max(0, Math.floor(seconds))}`;
}

/**
 * A recording named the way the Tribunal numbers its sessions. The number alone is not
 * unique (it restarts with the 2011 law and extraordinary sessions count apart), which is
 * why the kind and the year always travel with it.
 */
export function recordingNumberLabel(kind: string | null, number: number | null): string {
  if (number === null) return '';
  return kind === 'EXTRAORDINARY' ? `${number}ª SEJ` : `${number}ª SOJ`;
}


/** Lower-case without accents: the form a typed word and a passage are compared in. */
export function foldText(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** The words of a find-in-page query, folded; blank words dropped. */
export function findTerms(query: string): string[] {
  return foldText(query)
    .split(/\s+/)
    .filter((term) => term.length > 0);
}

/**
 * Whether a passage holds EVERY term. A reader typing "mercado relevante" wants the passages
 * with both words, in either order; a phrase match would miss the ones the speaker split.
 */
export function holdsTerms(text: string, terms: string[]): boolean {
  if (terms.length === 0) return false;
  const folded = foldText(text);
  return terms.every((term) => folded.includes(term));
}

export interface TextPart {
  text: string;
  hit: boolean;
}

/**
 * The passage cut into plain and matching parts, original text and accents intact (a reader
 * quotes what they see). Matching runs on the folded copy; when folding changes the length
 * of the text the offsets would not carry back, so the passage is returned unmarked.
 */
export function splitOnTerms(text: string, terms: string[]): TextPart[] {
  const folded = foldText(text);
  if (terms.length === 0 || folded.length !== text.length) return [{ text, hit: false }];
  const marked = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    for (let at = folded.indexOf(term); at !== -1; at = folded.indexOf(term, at + 1)) {
      for (let i = at; i < at + term.length; i += 1) marked[i] = true;
    }
  }
  const parts: TextPart[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = parts[parts.length - 1];
    if (last && last.hit === marked[i]) last.text += text[i];
    else parts.push({ text: text[i], hit: marked[i] });
  }
  return parts;
}
